/**
 * touchQueue — the sends of `POST /touch` one at a time. The route reads the session and commits it whole, so two
 * writes in flight would each start from the same session and the later commit would erase the earlier one. A send
 * waits for the one before it to answer; only the answer that leaves nothing queued behind it is applied, so an
 * older answer can never set an older profile key over a newer one.
 *
 * Whether the wall is read again is decided here, by the body: a tile touch (arming, a chip tap) moves the key and
 * leaves the wall as the visitor sees it; a body that steers (a lean word dropped, its Undo) is the visitor asking for
 * another order, so the answer that lands it is handed on as `steered`, true when any body sent since the last applied
 * answer steered.
 */

export type TouchBody = {tile: number} | {suppress: string} | {unsuppress: string}

/** A body that asks for another order of the wall, as opposed to one that only records what the visitor did. */
export function steers(body: TouchBody): boolean {
    return !('tile' in body)
}

/** What the route answers: the profile key. */
export type TouchAnswer = {profile: string}

export function createTouchQueue(
    send: (body: TouchBody) => Promise<TouchAnswer | null>,
    apply: (answer: TouchAnswer, steered: boolean) => void,
): (body: TouchBody) => void {
    const queued: TouchBody[] = []
    let inFlight = false
    let steeredSinceApplied = false

    const next = async (): Promise<void> => {
        const body = queued.shift()
        if (!body) {
            inFlight = false
            return
        }
        inFlight = true
        if (steers(body)) steeredSinceApplied = true
        let answer: TouchAnswer | null = null
        try {
            answer = await send(body)
        } catch {
            answer = null
        }
        if (answer && queued.length === 0) {
            const steered = steeredSinceApplied
            steeredSinceApplied = false
            apply(answer, steered)
        }
        await next()
    }

    return (body) => {
        queued.push(body)
        if (!inFlight) void next()
    }
}

/** An answer's body read for what the page keeps, or `null` for anything that is not an answer. */
export function readTouchAnswer(data: unknown): TouchAnswer | null {
    if (typeof data !== 'object' || data === null) return null
    const {profile} = data as {profile?: unknown}
    if (typeof profile !== 'string') return null
    return {profile}
}
